import { axios, getResponse, isUndNullEmp, settings } from '../../helpers/main';
import { userAuthContextType } from '../../auth/AuthProvider';

const { backend } = settings;
const authServices = { user: userAuthContextType }; //eslint-disable-line
const objectServices = getResponse({ type: 0 }); //eslint-disable-line

/**
 * Service to manage communication with the Analysts/Users API.
 * Follows the Single Responsibility Principle (SRP) by separating network logic from the UI.
 */
export const AnalystService = {
    /**
     * Retrieves all Analysts.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    getAll: async ({
        auth,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'AnalystService.getAll';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.users}/`,
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    method: backend?.methods?.users.getAnalysts,
                    languageId: languageId,
                },
                subMethod: subMethod || currentMethod,
            }
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Retrieves data for adding an Analyst (roles, etc).
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    getAnalystRoles: async ({
        auth,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'AnalystService.getAnalystRoles';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    languageId: languageId,
                    method: backend?.methods?.roles.getAnalysts,
                },
                url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.roles}/`,
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Retrieves an Analyst by ID with companies access.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Analyst ID.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    getById: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'AnalystService.getById';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    id: id,
                    languageId: languageId,
                    method: backend?.methods?.users.getAnalystByIdWithCompaniesAccess,
                },
                url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.users}/`,
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Deletes an Analyst by ID.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Analyst ID to delete.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    deleteAsync: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'AnalystService.deleteAsync';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.users}/${id}`,
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    languageId: languageId,
                },
                subMethod: subMethod || currentMethod,
            };
            response = await axios.delete(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },
};
